import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Container } from "../Users/styles";
import { Button, Input, Select, TableColumnsType, Tooltip } from "antd";
import { SearchOutlined, CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { convertUtcToLocal } from "../../utils/helpers/convertUtcToLocal";
import { getAllActivityLogs } from "../../redux/features/logs/logsAsyncThunk";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectLogs } from "../../redux/features/logs/logsSlice";
import { ActivityLogOptions, ActivityLogTypeValue, BackendActivityLogType, DefaultUserName, getActivityLogText, LostItemActivityLogType, LostItemLogsData } from "../../types/ActivityLog";
import { RequestResults } from "../../redux/common/RequestStatus";
import { ClaimButton, LogsDescription, LogsTable } from "../EmailLogs/styles";
import SystemPayRoutes from "../../utils/constants/Routes";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { permissions } from "../../utils/constants/permissions";
import { fetchEstablishmentType } from "../../redux/features/establishmentType/establishmentAsyncThunk";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import DatePickerModal from "../../components/DatePickerModal";
import {
  FilterTabWrapper,
  StatusFilters,
  FilterInputs,
  StyledSelect
} from "../Customers/styles";
import {
  EnvironmentOutlined,
  CloudOutlined,
  BankOutlined,
  CarOutlined,
  RocketOutlined,
  SmileOutlined,
  TrophyOutlined,
  ApartmentOutlined,
  HomeOutlined,
  RestOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
dayjs.extend(isBetween);

const ActivityLogs = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(50);
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [selectedActivityLogs, setSelectedActivityLogs] = useState<string | undefined>();
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateField, setDateField] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const { activityLogs: { data: activityLogs, status, counts } } = useAppSelector(selectLogs);
  const { data: establishmentTypes, status: establishmentTypeStatus } = useAppSelector(selectEstablishmentType);
  const dispatch = useAppDispatch();

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSearchFieldChange = (value: string | null) => {
    setSearchField(value || undefined);
    setCurrentPage(1);
  };

  const handleActivityLogsSearchFieldChange = (value: string | null) => {
    setSelectedActivityLogs(value || undefined);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1);
  };

  const addKeyProperty = (data: LostItemLogsData[]) => {
    return data.map((item: LostItemLogsData) => ({ ...item, key: item.id }));
  };

  const getActivityTypeText = (type: LostItemActivityLogType) => {
    return getActivityLogText(type);
  };

  const handleClickOnClaimNumber = (record: LostItemLogsData) => {
    if (record?.itemNumber) {
      window.open(`${SystemPayRoutes.foundReportBase}/${record?.itemNumber}?withDeleted=true`, '_blank');
    }
    else if (record?.trackingId) {
      window.open(`${SystemPayRoutes.reportBase}/${record?.trackingId}?withDeleted=true`, '_blank');
    }
  };

  const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
    setDateField(params.dateField);
    setFromDate(params.fromDate);
    setToDate(params.toDate);
    setIsDateModalOpen(false);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setDateField(undefined);
    setFromDate(undefined);
    setToDate(undefined);
    setCurrentPage(1);
  };

  const userRole = useAppSelector(selectUserRole);
  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );
  const isAccessPaymentLogs = useMemo(() => hasPermission(permissions.IsPaymentLogAllowed), [hasPermission]);

  const columns: TableColumnsType<any> = useMemo(() => {
    return [
      { title: "#", key: "index", render: (_, __, index) => `#${index + 1 + (currentPage - 1) * currentLimit}` },
      { title: "Done By", dataIndex: "adminName", key: "adminName", render: (_, record: LostItemLogsData) => <>{record.userName || record.adminName || DefaultUserName || "-"}</> },
      { title: "Claim Number", dataIndex: "trackingId", key: "trackingId", render: (_, record: LostItemLogsData) => <ClaimButton type="link" onClick={() => handleClickOnClaimNumber(record)}>{record.trackingId || record.itemNumber || "-"}</ClaimButton> },
      {
        title: "Activity Type", dataIndex: "type", key: "type", render: (_, record: LostItemLogsData) => <>{getActivityTypeText(record.type as any)}</>
      },
      {
        className: "activity-cell",
        title: "Activity", dataIndex: "description", key: "description", render: (_, record: LostItemLogsData) =>
          <Tooltip title={record.description + (record.note ? ` ${record.note}` : "")}><LogsDescription>{record.description + (record.note ? ` ${record.note}` : "")}</LogsDescription></Tooltip>
      },
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => <div>{date ? convertUtcToLocal(date, "MM/DD/YYYY hh:mm A") : "-"}</div>,
      },
    ];
  }, [activityLogs, currentPage, currentLimit]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 1000);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    if (establishmentTypeStatus === "idle") {
      dispatch(fetchEstablishmentType());
    }
  }, [dispatch, establishmentTypeStatus]);

  useEffect(() => {
    const searchValueParam = searchField === ActivityLogTypeValue ? selectedActivityLogs?.toString() : debouncedSearchValue?.trim();
    dispatch(
      getAllActivityLogs({
        offset: (currentPage - 1) * currentLimit,
        limit: currentLimit,
        searchField,
        searchValue: searchValueParam,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        dateField: fromDate && toDate ? "activityLog.createdAt" : "",
        keepPaymentLog: isAccessPaymentLogs,
        establishmentType: activeFilter
      })
    );
  }, [dispatch, currentPage, currentLimit, searchField, debouncedSearchValue, fromDate, toDate, selectedActivityLogs, activeFilter, isAccessPaymentLogs]);

  const establishmentIconPool = useMemo(
    () => [
      <EnvironmentOutlined style={{ fontSize: 16 }} />,
      <CloudOutlined style={{ fontSize: 16 }} />,
      <BankOutlined style={{ fontSize: 16 }} />,
      <CarOutlined style={{ fontSize: 16 }} />,
      <RocketOutlined style={{ fontSize: 16 }} />,
      <SmileOutlined style={{ fontSize: 16 }} />,
      <TrophyOutlined style={{ fontSize: 16 }} />,
      <ApartmentOutlined style={{ fontSize: 16 }} />,
      <HomeOutlined style={{ fontSize: 16 }} />,
      <RestOutlined style={{ fontSize: 16 }} />,
      <DollarCircleOutlined style={{ fontSize: 16 }} />,
    ],
    []
  );

  const establishmentTypeOptions = useMemo(() => {
    return establishmentTypes.map((establishmentType) => ({
      label: establishmentType.name,
      value: establishmentType.id,
      key: establishmentType.id,
      fullUrl: establishmentType.fullUrl,
    }));
  }, [establishmentTypes]);

  const establishmentSelectOptions = useMemo(() => {
    const mappedOptions = establishmentTypeOptions.map((filter: any) => {
      const randomIndex = Math.floor(Math.random() * establishmentIconPool.length);
      const icon = establishmentIconPool[randomIndex];
      return {
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {filter?.fullUrl ? (
              <img src={filter.fullUrl} alt={filter.label} style={{ width: 18, height: 18 }} />
            ) : (
              <div style={{ fontSize: 18 }}>{icon}</div>
            )}
            <span>{filter.label}</span>
          </div>
        ),
        value: filter.key,
      };
    });
    return [{ label: "All Establishments", value: null }, ...mappedOptions];
  }, [establishmentTypeOptions, establishmentIconPool]);

  const activityLogOptions = Object.keys(BackendActivityLogType)
    .filter(key => isNaN(Number(key))) // Filter out numeric keys
    .map(key => {
      const value = BackendActivityLogType[key as keyof typeof BackendActivityLogType];
      return {
        label: getActivityLogText(value),
        value: value,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const isDateRangeSelected = useMemo(() => !!(fromDate && toDate), [fromDate, toDate]);

  const formattedDateRange = useMemo(() => {
    return fromDate && toDate
      ? `${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`
      : "Date Range";
  }, [fromDate, toDate]);

  return (
    <Container>
      <FilterTabWrapper>
        <StatusFilters>
          <StyledSelect
            style={{ minWidth: 200 }}
            options={establishmentSelectOptions}
            value={activeFilter}
            onChange={(value) => setActiveFilter(value as string | null)}
            placeholder="Establishment"
            size="middle"
          />
        </StatusFilters>

        <FilterInputs>
          <StyledSelect
            allowClear
            value={searchField}
            options={ActivityLogOptions}
            onChange={(value) => handleSearchFieldChange(value as string | null)}
            placeholder="Filter by"
            size="middle"
          />
          {searchField === ActivityLogTypeValue ? (
            <Select
              style={{ width: 200 }}
              options={activityLogOptions}
              allowClear
              onChange={handleActivityLogsSearchFieldChange}
              value={selectedActivityLogs}
              placeholder="Select activity type"
            />
          ) : (
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              style={{ width: 200 }}
            />
          )}
          <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setIsDateModalOpen(true)}
              type={isDateRangeSelected ? "primary" : "default"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
            >
              {isDateRangeSelected && (
                <CloseCircleOutlined
                  style={{ marginLeft: 4 }}
                  onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }}
                />
              )}
            </Button>
          </Tooltip>
        </FilterInputs>
      </FilterTabWrapper>

      <LogsTable
        columns={columns}
        dataSource={addKeyProperty(activityLogs)}
        loading={status === RequestResults.pending}
        pagination={{
          current: currentPage,
          total: counts,
          pageSize: currentLimit,
          showSizeChanger: true,
          onChange: handlePageChange,
          onShowSizeChange: (_: any, size: number) => handleLimitChange(size),
        }}
      />

      <DatePickerModal
        isModalOpen={isDateModalOpen}
        handleOk={handleDateModalOk}
        handleCancel={() => setIsDateModalOpen(false)}
        values={{ dateField, fromDate, toDate }}
      />
    </Container>
  );
};

export default ActivityLogs;
